import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { ChevronUp, Volume2, VolumeX, Pause, Play } from 'lucide-react';
import { AffiliateDisclosure } from '@/components/AffiliateFooter';
import { motion, AnimatePresence } from 'framer-motion';

export default function LandingPage() {
  const navigate = useNavigate();
  const [footerVisible, setFooterVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUnmuteHint, setShowUnmuteHint] = useState(true);
  const videoRef = useRef(null);
  const bgVideoRef = useRef(null);
  const footerTimeoutRef = useRef(null);
  const firstInteractionDoneRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, []);

  // Make sure the mobile-only aesthetic background video starts loading + playing
  // even though it lives behind a `hidden` class on desktop. Some browsers skip
  // network/decoding for elements that initially mount with display:none and
  // never recover when the media query flips it to block.
  useEffect(() => {
    const bg = bgVideoRef.current;
    if (!bg) return;
    bg.muted = true;
    try { bg.load(); } catch (_) {}
    const tryPlay = () => bg.play().catch(() => {});
    tryPlay();
    const mq = window.matchMedia('(max-width: 639px) and (orientation: portrait)');
    const onChange = () => { if (mq.matches) tryPlay(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    // also retry when window regains focus / orientation changes
    window.addEventListener('orientationchange', tryPlay);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
      window.removeEventListener('orientationchange', tryPlay);
    };
  }, []);

  // Unmute on first interaction anywhere
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (firstInteractionDoneRef.current) return;
      firstInteractionDoneRef.current = true;
      const v = videoRef.current;
      if (v) {
        v.muted = false;
        setIsMuted(false);
        v.play().catch(() => {});
      }
      const bg = bgVideoRef.current;
      if (bg) {
        try { bg.load(); } catch (_) {}
        bg.play().catch(() => {});
      }
      setShowUnmuteHint(false);
    };
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    events.forEach(e => window.addEventListener(e, handleFirstInteraction, { once: true, passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handleFirstInteraction));
  }, []);

  const toggleMute = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
    setShowUnmuteHint(false);
    if (!v.muted) v.play().catch(() => {});
  };

  const togglePlay = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const handleFooterEnter = () => {
    if (footerTimeoutRef.current) clearTimeout(footerTimeoutRef.current);
    setFooterVisible(true);
  };
  const handleFooterLeave = () => {
    footerTimeoutRef.current = setTimeout(() => setFooterVisible(false), 400);
  };

  return (
    <div className="h-screen w-screen bg-black relative overflow-hidden" data-testid="landing-page">
      {/* Aesthetic muted background video — only on mobile portrait, fills the screen so the trailer can sit centered with no black bars */}
      <video
        ref={bgVideoRef}
        className="fixed inset-0 w-full h-full object-cover z-0 hidden max-sm:portrait:block"
        autoPlay
        loop
        playsInline
        muted
        preload="auto"
        aria-hidden="true"
        data-testid="aesthetic-bg-video"
      >
        <source src="/aesthetic-bg.mp4" type="video/mp4" />
      </video>

      {/* Main trailer video — desktop/tablet & mobile-landscape fill the screen (object-cover, unchanged from before).
          Mobile portrait: object-contain so the full trailer is visible (fit-to-screen), centered, with the aesthetic video showing behind the letterboxed area. */}
      <video
        ref={videoRef}
        className="fixed inset-0 w-full h-full object-cover max-sm:portrait:object-contain z-10"
        autoPlay
        loop
        playsInline
        muted
        preload="auto"
        data-testid="hero-video"
        onError={() => {}}
      >
        <source src="/brand-os-trailer.mp4" type="video/mp4" />
        <source src="/brand-os-trailer.webm" type="video/webm" />
      </video>

      {/* Soft vignette — only on mobile portrait, sits above the aesthetic bg & around the centered trailer to blend the edges */}
      <div
        className="pointer-events-none fixed inset-0 z-[11] hidden max-sm:portrait:block"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0) 38%, rgba(0,0,0,0.45) 72%, rgba(0,0,0,0.78) 100%)',
        }}
        aria-hidden="true"
      />

      {/* Top bar: just logo + sign-in pill (transparent so they don't fight the video) */}
      <header className="fixed top-0 left-0 right-0 z-20 flex items-center justify-between px-4 pt-4 sm:px-6 sm:pt-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 group"
          data-testid="brand-logo"
        >
          <div
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, #d8b4fe 0%, #c084fc 40%, #e9d5ff 70%, #c084fc 100%)',
              boxShadow: '0 4px 16px rgba(192, 132, 252, 0.5), inset 0 1px 0 rgba(255,255,255,0.5)',
            }}
          >
            <span className="text-white font-bold text-base sm:text-lg" style={{ fontFamily: "'Manrope', sans-serif", textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>B</span>
          </div>
          <span
            className="hidden sm:inline text-sm font-semibold tracking-tight text-white/90 drop-shadow-md group-hover:text-white transition"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Brand OS
          </span>
        </button>

        <button
          onClick={() => navigate('/auth')}
          className="px-4 py-2 sm:px-5 sm:py-2 rounded-full text-xs sm:text-sm font-medium tracking-wide bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-white/20 active:scale-95 transition"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="top-signin-btn"
        >
          Sign in
        </button>
      </header>

      {/* Floating CTA — bottom-center, minimal, doesn't block the video */}
      <div className="fixed bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-20">
        <motion.button
          data-testid="hero-get-started-btn"
          onClick={() => navigate('/auth')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-7 py-3 sm:px-9 sm:py-3.5 rounded-full text-xs sm:text-sm font-semibold tracking-wide whitespace-nowrap"
          style={{
            fontFamily: "'Manrope', sans-serif",
            background: 'linear-gradient(145deg, #c084fc, #a855f7)',
            color: 'white',
            boxShadow: '0 8px 32px rgba(168, 85, 247, 0.55), inset 0 1px 0 rgba(255,255,255,0.25)',
          }}
        >
          Start Your Journey · Free
        </motion.button>
      </div>

      {/* Floating video controls — bottom right, glassy */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-30 flex items-center gap-2">
        <AnimatePresence>
          {showUnmuteHint && isMuted && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              className="hidden sm:block px-3 py-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-white text-xs font-medium"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              tap for sound
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/40 backdrop-blur-md border border-white/15 hover:bg-black/60 flex items-center justify-center transition"
          data-testid="video-play-pause-btn"
        >
          {isPlaying ? <Pause className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" fill="white" /> : <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white ml-0.5" fill="white" />}
        </button>
        <button
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/40 backdrop-blur-md border border-white/15 hover:bg-black/60 flex items-center justify-center transition"
          data-testid="video-mute-btn"
        >
          {isMuted ? <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" /> : <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />}
        </button>
      </div>

      {/* Footer reveal trigger - tiny chevron at bottom */}
      <div
        className="fixed bottom-0 left-0 right-0 z-20 flex flex-col items-center pointer-events-none"
        data-testid="footer-trigger"
      >
        <div
          className="pointer-events-auto"
          onMouseEnter={handleFooterEnter}
          onMouseLeave={handleFooterLeave}
        >
          <motion.button
            onClick={() => setFooterVisible(v => !v)}
            className="flex items-center justify-center w-10 h-8 cursor-pointer"
            animate={{ opacity: footerVisible ? 0 : 0.6 }}
            transition={{ duration: 0.3 }}
            aria-label="Toggle footer"
          >
            <ChevronUp className="w-4 h-4 text-white" strokeWidth={1.5} />
          </motion.button>
        </div>

        <AnimatePresence>
          {footerVisible && (
            <motion.footer
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
              className="w-full bg-black/85 backdrop-blur-xl border-t border-white/10 py-5 px-4 sm:px-6 pointer-events-auto"
              data-testid="landing-footer"
              onMouseEnter={handleFooterEnter}
              onMouseLeave={handleFooterLeave}
            >
              <div className="max-w-5xl mx-auto">
                <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs sm:text-sm text-white/60 mb-3">
                  <button onClick={() => navigate('/blog')} className="hover:text-purple-300 transition" data-testid="footer-blog">Blog</button>
                  <button onClick={() => navigate('/faq')} className="hover:text-purple-300 transition" data-testid="footer-faq">FAQ</button>
                  <button onClick={() => navigate('/auth')} className="hover:text-purple-300 transition" data-testid="footer-signin">Sign In</button>
                  <button onClick={() => navigate('/privacy')} className="hover:text-purple-300 transition" data-testid="footer-privacy">Privacy</button>
                  <button onClick={() => navigate('/terms')} className="hover:text-purple-300 transition" data-testid="footer-terms">Terms</button>
                  <a href="mailto:support@buildbrandos.com" className="hover:text-purple-300 transition" data-testid="footer-contact">Contact</a>
                </nav>
                <div className="text-center text-[10px] text-white/30 mb-2">&copy; {new Date().getFullYear()} Brand OS. All rights reserved.</div>
                <div className="text-center text-white/40 max-w-2xl mx-auto"><AffiliateDisclosure /></div>
              </div>
            </motion.footer>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
